/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import { CampaignSearchParams } from '../types';
import { useCampaignConfigBuilderApi } from '../api/CampaignConfigBuilderApi';
import { useGetAppointmentTypesApi } from 'src/modules/calendar/api';
import { useGetContactCustomFieldsApi } from '../../../api/customFields';
import { useGroupsApi } from 'src/modules/groups/api/GroupsApi';
import { useKeywordsApi } from 'src/modules/keywords/api/KeywordsApi';

/**
 * Service for managing campaign configurations
 */
export const useCampaignConfigService = () => {
  const api = useCampaignConfigBuilderApi();
  const groupsApi = useGroupsApi();
  const getAppointmentTypesApi = useGetAppointmentTypesApi();
  const keywordsApi = useKeywordsApi();
  const getCustomFieldsApi = useGetContactCustomFieldsApi();

  const getActiveCampaignConfigCategories = useCallback(async () => {
    try {
      const response = await api.getCampaignConfigCategories({
        statuses: 'active',
        sort: 'rank_asc',
        limit: 100,
        offset: 0,
      });
      return response.data.items;
    } catch (error) {
      console.error('Failed to fetch active campaign config categories:', error);
      throw error;
    }
  }, []);

  const getCampaignConfigs = useCallback(async (params: CampaignSearchParams) => {
    try {
      const response = await api.getCampaignConfigs(params);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch campaign configs:', error);
      throw error;
    }
  }, [api]);

  const getUsedCampaignConfigs = useCallback(async (applicationIds?: string) => {
    try {
      const response = await api.getUsedCampaignConfigs(applicationIds);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch used campaign configs:', error);
      throw error;
    }
  }, [api]);

  const getCampaignConfig = useCallback(async (id: string) => {
    try {
      const response = await api.getCampaignConfig(id);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch campaign config:', error);
      throw error;
    }
  }, [api]);

  const createCampaignConfig = useCallback(async (data: any) => {
    try {
      const response = await api.createCampaignConfig(data);
      return response.data;
    } catch (error) {
      console.error('Failed to create campaign config:', error);
      throw error;
    }
  }, [api]);

  const updateCampaignConfig = useCallback(async (id: string, data: any) => {
    try {
      const response = await api.updateCampaignConfig(id, data);
      return response.data;
    } catch (error) {
      console.error('Failed to update campaign config:', error);
      throw error;
    }
  }, [api]);

  const setCampaignConfigFavorite = useCallback(async (id: string, companyId: string) => {
    try {
      await api.setCampaignConfigFavorite(id, companyId);
    } catch (error) {
      console.error('Failed to set campaign config favorite:', error);
      throw error;
    }
  }, [api]);

  const unsetCampaignConfigFavorite = useCallback(async (id: string, companyId: string) => {
    try {
      await api.unsetCampaignConfigFavorite(id, companyId);
    } catch (error) {
      console.error('Failed to unset campaign config favorite:', error);
      throw error;
    }
  }, [api]);

  /**
   * Get campaign config by type
   */
  const getCampaignConfigByType = useCallback(async (type: string, applicationId?: string) => {
    try {
      const response = await api.getCampaignConfigs({
        types: type,
        statuses: 'active',
        sort: 'date_created_desc',
        limit: 1,
        offset: 0,
        applicationIds: applicationId,
      });
      
      if (response.data.items.length > 0) {
        return response.data.items[0];
      }
      
      return null;
    } catch (error) {
      console.error('Failed to fetch campaign config by type:', error);
      throw error;
    }
  }, [api]);

  const getAppointmentTypes = useCallback(async (companyId: string) => {
    try {

      const response = await getAppointmentTypesApi({
        companyId,
        statuses: 'active',
        limit: 100,
        offset: 0,
      });
      return response.data.items.map(item => ({
        text: item.name,
        value: item.id,
      }));
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
      return [];
    }
  }, []);

  const getCustomFields = useCallback(async (companyId: string) => {
    try {
      const response = await getCustomFieldsApi({
        companyId,
      });
      return response.data.items.map(item => ({
        text: item.description,
        value: item.name,
      }));
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
      return [];
    }
  }, []);

  const getGroups = useCallback(async (companyId: string) => {
    try {
      const response = await groupsApi.getGroups({
        companyId,
        statuses: 'active',
        limit: 100,
        offset: 0,
      });
      return response.data.items.map((item: any) => ({
        text: item.name,
        value: item.id,
      }));
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      return [];
    }
  }, []);

  const getKeywords = useCallback(async (companyId: string) => {
    try {
      const response = await keywordsApi.getKeywords({
        companyId,
        limit: 100,
        offset: 0,
      });
      return response.data.items.map((item: any) => ({
        text: item.name,
        value: item.id,
      }));
    } catch (error) {
      console.error('Failed to fetch keywords:', error);
      return [];
    }
  }, []);

  const getCampaignConfigCategories = useCallback(async (params?: any) => {
    try {
      const response = await api.getCampaignConfigCategories({
        limit: params?.limit || 100,
        offset: params?.offset || 0,
        sort: params?.sort || 'rank_asc',
        statuses: params?.statuses,
        ...params,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch campaign config categories:', error);
      throw error;
    }
  }, [api]);

  const getCampaignConfigCategory = useCallback(async (name: string) => {
    try {
      const response = await api.getCampaignConfigCategory(name);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch campaign config category:', error);
      throw error;
    }
  }, [api]);

  const createCampaignConfigCategory = useCallback(async (data: any) => {
    try {
      const response = await api.createCampaignConfigCategory(data);
      return response.data;
    } catch (error) {
      console.error('Failed to create campaign config category:', error);
      throw error;
    }
  }, [api]);

  const updateCampaignConfigCategory = useCallback(async (name: string, data: any) => {
    try {
      const response = await api.updateCampaignConfigCategory(name, data);
      return response.data;
    } catch (error) {
      console.error('Failed to update campaign config category:', error);
      throw error;
    }
  }, [api]);

  const deleteCampaignConfigCategories = useCallback(async (names: string[]) => {
    try {
      await api.deleteCampaignConfigCategories({ names });
    } catch (error) {
      console.error('Failed to delete campaign config categories:', error);
      throw error;
    }
  }, [api]);

  return {
    getActiveCampaignConfigCategories,
    getCampaignConfigs,
    getUsedCampaignConfigs,
    getCampaignConfig,
    getCampaignConfigByType,
    createCampaignConfig,
    updateCampaignConfig,
    setCampaignConfigFavorite,
    unsetCampaignConfigFavorite,
    getAppointmentTypes,
    getCustomFields,
    getGroups,
    getKeywords,
    getCampaignConfigCategories,
    getCampaignConfigCategory,
    createCampaignConfigCategory,
    updateCampaignConfigCategory,
    deleteCampaignConfigCategories,
  };
};
